import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createChat, addMessageToChatStreaming } from '../../services/chatService';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import ConversationStarters from './ConversationStarters';
import ChatInput from './ChatInput';
import '../../styles/animations.css';

function WelcomeChat() {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  
  const { currentUser } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();
  
  // Create a new chat and send first message
  async function handleSendMessage(message, files = []) {
    if (!message.trim() || sending) return;
    
    try {
      setSending(true);
      console.log('Creating new chat and preparing message...');
      
      // Create a new chat
      const newChat = await createChat(currentUser.uid);
      console.log('New chat created with ID:', newChat.id);
      
      // Dispatch a custom event for sidebar to update
      const newChatEvent = new CustomEvent('new-chat-created', { 
        detail: { newChat }
      });
      window.dispatchEvent(newChatEvent);
      
      // Store chat ID in localStorage to ensure it's picked up after navigation
      localStorage.setItem('justCreatedChatId', newChat.id);
      
      // Store both message and file information (if any) in localStorage
      const pendingMessageData = {
        text: message,
        timestamp: new Date().toISOString(),
        hasFiles: files.length > 0
      };
      
      console.log('Storing pending message data in localStorage');
      localStorage.setItem('pendingMessage', JSON.stringify(pendingMessageData));
      
      // If there are files, we need to store their data temporarily
      // This is a simplistic approach; in a real app, you'd handle larger files differently
      if (files.length > 0) {
        try {
          console.log('Storing file information for', files.length, 'files');
          // Store minimal file info - full file objects can't be stored in localStorage
          const fileInfoArray = files.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified
          }));
          localStorage.setItem('pendingFiles', JSON.stringify(fileInfoArray));
          
          // Note: The actual file contents can't be stored in localStorage
          // In a real implementation, you might want to upload them to a temporary 
          // storage or use IndexedDB instead
        } catch (fileError) {
          console.error('Error storing file information:', fileError);
        }
      }
      
      // Longer delay before navigation to ensure localStorage is updated
      console.log('Waiting before navigation...');
      setTimeout(() => {
        console.log('Now navigating to new chat...');
        // Navigate to the new chat
        navigate(`/chat/${newChat.id}`);
      }, 1000); // Increased to 1 second
      
    } catch (error) {
      console.error('Error creating new chat:', error);
      setSending(false);
      setError('Failed to create a new chat. Please try again.');
    }
  }
  
  // Handle starter selection (same as sending a message)
  const handleStarterSelect = (starter) => {
    handleSendMessage(starter);
  };
  
  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Main content area with conversation starters */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        <div className="w-full h-full flex items-center justify-center p-4 animate-fadeIn duration-700">
          <ConversationStarters 
            onSelectStarter={handleStarterSelect}
          />
        </div>
      </div>
      
      {/* Chat input */}
      <div className="p-4 border-t border-gray-200 bg-white shadow-inner transition-all duration-300">
        <ChatInput onSendMessage={handleSendMessage} disabled={sending} />
        
        {/* Optional typing status indicator */}
        {sending && (
          <div className="text-xs text-indigo-500 mt-1 pl-2 flex items-center">
            <span className="animate-pulse mr-1">●</span> Creating your chat...
          </div>
        )}
        
        {/* Error message if needed */}
        {error && (
          <div className="text-xs text-red-500 mt-1 pl-2">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default WelcomeChat; 